import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import {
    FaCheck,
    FaTimes,
    FaHourglass,
    FaCalendar,
    FaUser
} from 'react-icons/fa';

const ManagerLeaves = () => {
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');

    const [stats, setStats] = useState({
        all: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });

    useEffect(() => {
        fetchLeaves();
    }, [filter]);

    const fetchLeaves = async () => {
        try {
            setLoading(true);

            const params = filter !== 'all' ? { status: filter } : {};
            const response = await api.get('/leaves', { params });

            if (response.data.success) {

                // ✅ FIXED HERE
                const rawLeaves = response.data.data;

                // ❗ remove managers (important)
                const employeeLeaves = rawLeaves.filter(
                    l => !l.user?.role?.name?.toLowerCase().includes('manager')
                );

                setLeaves(employeeLeaves);

                setStats({
                    all: employeeLeaves.length,
                    pending: employeeLeaves.filter(l => l.status === 'pending').length,
                    approved: employeeLeaves.filter(l => l.status === 'approved').length,
                    rejected: employeeLeaves.filter(l => l.status === 'rejected').length
                });
            }

        } catch (error) {
            toast.error('Failed to fetch leave requests');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ SAME AS YOUR WORKING SCRIPT
    const updateStatus = async (leaveId, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this leave request?`)) {
            return;
        }

        try {
            const response = await api.put(`/leaves/${leaveId}/status`, { status });

            if (response.data.success) {
                toast.success(`Leave ${status} successfully`);
                fetchLeaves();
            }
        } catch (error) {
            toast.error('Failed to update leave status');
            console.error(error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'text-green-600 bg-green-100';
            case 'rejected': return 'text-red-600 bg-red-100';
            case 'pending': return 'text-yellow-600 bg-yellow-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <FaCheck className="inline mr-1" />;
            case 'rejected': return <FaTimes className="inline mr-1" />;
            case 'pending': return <FaHourglass className="inline mr-1" />;
            default: return null;
        }
    };

    const filteredLeaves =
        filter === 'all'
            ? leaves
            : leaves.filter(l => l.status === filter);

    return (
        <div className="p-6">

            {/* HEADER */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold">Team Leave Requests</h1>
                <p className="text-gray-600 mt-2">
                    Approve employee leave requests
                </p>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">

                <Stat title="Total" value={stats.all} icon={<FaCalendar />} />
                <Stat title="Pending" value={stats.pending} icon={<FaHourglass />} />
                <Stat title="Approved" value={stats.approved} icon={<FaCheck />} />
                <Stat title="Rejected" value={stats.rejected} icon={<FaTimes />} />

            </div>

            {/* FILTER TABS */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="flex border-b">
                    {['all', 'pending', 'approved', 'rejected'].map(key => (
                        <button
                            key={key}
                            onClick={() => setFilter(key)}
                            className={`px-6 py-3 font-medium ${filter === key
                                    ? 'border-b-2 border-blue-600 text-blue-600'
                                    : 'text-gray-600 hover:text-blue-600'
                                }`}
                        >
                            {key.toUpperCase()} ({stats[key]})
                        </button>
                    ))}
                </div>
            </div>

            {/* TABLE */}
            <div className="bg-white rounded-lg shadow overflow-hidden">

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin h-12 w-12 border-b-2 border-blue-600 rounded-full mx-auto"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">

                        <table className="min-w-full divide-y">

                            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="px-6 py-3 text-left">Employee</th>
                                    <th className="px-6 py-3 text-left">Type</th>
                                    <th className="px-6 py-3 text-left">Dates</th>
                                    <th className="px-6 py-3 text-left">Reason</th>
                                    <th className="px-6 py-3 text-left">Status</th>
                                    <th className="px-6 py-3 text-left">Actions</th>
                                </tr>
                            </thead>

                            <tbody className="divide-y">

                                {filteredLeaves.map(leave => (
                                    <tr key={leave._id} className="hover:bg-gray-50">

                                        <td className="px-6 py-4 flex items-center gap-3">
                                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <FaUser className="text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium">{leave.user?.name}</p>
                                                <p className="text-sm text-gray-500">{leave.user?.email}</p>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 capitalize">{leave.type}</td>

                                        <td className="px-6 py-4 text-sm">
                                            {new Date(leave.startDate).toLocaleDateString()} -
                                            <br />
                                            {new Date(leave.endDate).toLocaleDateString()}
                                        </td>

                                        <td className="px-6 py-4 max-w-xs truncate">
                                            {leave.reason}
                                        </td>

                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(leave.status)}`}>
                                                {getStatusIcon(leave.status)}
                                                {leave.status}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4">
                                            {leave.status === 'pending' && (
                                                <div className="flex gap-2">

                                                    <button
                                                        onClick={() => updateStatus(leave._id, 'approved')}
                                                        className="p-2 bg-green-100 text-green-600 rounded hover:bg-green-200"
                                                    >
                                                        <FaCheck />
                                                    </button>

                                                    <button
                                                        onClick={() => updateStatus(leave._id, 'rejected')}
                                                        className="p-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
                                                    >
                                                        <FaTimes />
                                                    </button>

                                                </div>
                                            )}
                                        </td>

                                    </tr>
                                ))}

                            </tbody>
                        </table>

                    </div>
                )}
            </div>
        </div>
    );
};

const Stat = ({ title, value, icon }) => (
    <div className="bg-white p-6 rounded shadow flex justify-between items-center">
        <div>
            <p className="text-gray-500 text-sm">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className="text-3xl text-blue-500">{icon}</div>
    </div>
);

export default ManagerLeaves;